import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, BadgeCheck, ArrowLeft, Check, Lock, Video, FileText, Image as ImageIcon, Loader2, Edit3, X, DollarSign, Plus, Save } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const NICHES = ["affiliate", "dropshipping", "freelance", "crypto", "content", "health", "ai"];

export default function LessonDetail() {
  const { id } = useParams();
  const { user: me } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const videoRef = useRef(); const pdfRef = useRef(); const slideRef = useRef(); const coverRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/lessons/${id}`);
      setLesson(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const complete = async () => {
    await api.post(`/lessons/${id}/complete`);
    setLesson((l) => ({ ...l, completed_by_me: true }));
  };

  const requestAccess = async () => {
    setRequesting(true);
    try {
      const { data } = await api.post(`/lessons/${id}/request`);
      setLesson((l) => ({ ...l, purchase_status: data.status }));
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to request access.");
    } finally { setRequesting(false); }
  };

  const openEdit = () => {
    setForm({
      title: lesson.title || "",
      description: lesson.description || "",
      niche: lesson.niche || "affiliate",
      content: lesson.content || "",
      cover_url: lesson.cover_url || "",
      duration_min: lesson.duration_min || 5,
      is_paid: !!lesson.is_paid,
      price: lesson.price || 0,
      videos: lesson.videos || [],
      pdfs: lesson.pdfs || [],
      slides: lesson.slides || [],
    });
    setEditing(true);
  };

  const uploadEdit = async (file, kind) => {
    if (!file) return;
    setUploading(kind); setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0, maxContentLength: Infinity, maxBodyLength: Infinity,
        onUploadProgress: (e) => e.total && setUploadPct(Math.round((e.loaded / e.total) * 100)),
      });
      const entry = { url: data.url, name: data.filename || file.name, size: data.size };
      if (kind === "cover") {
        setForm((f) => ({ ...f, cover_url: data.url }));
      } else {
        const key = kind === "video" ? "videos" : kind === "pdf" ? "pdfs" : "slides";
        setForm((f) => ({ ...f, [key]: [...f[key], entry] }));
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(null); setUploadPct(0); }
  };

  const removeEdit = (kind, idx) => {
    const key = kind === "video" ? "videos" : kind === "pdf" ? "pdfs" : "slides";
    setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/lessons/${id}`, { ...form, price: Number(form.price || 0) });
      setLesson((l) => ({ ...l, ...data, has_access: l.has_access, purchase_status: l.purchase_status }));
      setEditing(false);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to save lesson.");
    } finally { setSaving(false); }
  };

  if (loading || !lesson) {
    return <Layout><div className="max-w-3xl mx-auto py-8 px-4 md:px-8"><div className="surface rounded-xl h-96 shimmer" /></div></Layout>;
  }

  const locked = lesson.is_paid && !lesson.has_access;
  const status = lesson.purchase_status;
  const canEdit = me && (me.user_id === lesson.author?.user_id || me.is_admin);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-8 px-4 md:px-8">
        <Link to="/lessons" className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-[#00E676] mb-5 transition font-mono">
          <ArrowLeft size={14} /> all lessons
        </Link>

        {canEdit && !editing && (
          <div className="flex items-center justify-end mb-4 gap-2">
            <button
              data-testid="edit-lesson-btn"
              onClick={openEdit}
              className="surface rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-white/[0.06] transition flex items-center gap-1.5"
            >
              <Edit3 size={13} /> Edit lesson
            </button>
            {me?.is_admin && me.user_id !== lesson.author?.user_id && (
              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-red-500/15 border border-red-500/30 text-red-300">
                admin override
              </span>
            )}
          </div>
        )}

        {editing && form && (
          <div className="surface rounded-xl p-5 mb-6 space-y-3" data-testid="lesson-edit-form">
            <div className="flex items-center justify-between">
              <div className="font-heading font-bold text-xl">Edit lesson</div>
              <button onClick={() => setEditing(false)} className="text-white/50 hover:text-white"><X size={16} /></button>
            </div>
            <input data-testid="edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Title" className="w-full bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 focus:outline-none focus:border-[#00E676]/40" />
            <input data-testid="edit-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Description" className="w-full bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40" />
            <textarea data-testid="edit-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={6} placeholder="Full content"
              className="w-full bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#00E676]/40" />
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="edit-niche" value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })}
                className="bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40">
                {NICHES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <input data-testid="edit-duration" type="number" value={form.duration_min}
                onChange={(e) => setForm({ ...form, duration_min: Number(e.target.value) })} placeholder="duration (min)"
                className="bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40" />
            </div>

            {/* Cover image uploader */}
            <div className="surface-elev rounded-md p-3 flex items-center gap-3">
              <input ref={coverRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden" data-testid="edit-cover-input"
                onChange={(e) => uploadEdit(e.target.files?.[0], "cover")} />
              {form.cover_url ? (
                <div className="relative">
                  <img src={form.cover_url} alt="" className="w-28 h-16 object-cover rounded-md border border-white/10" />
                  <button onClick={() => setForm({ ...form, cover_url: "" })}
                    className="absolute -top-1 -right-1 bg-black/80 rounded-full p-0.5 hover:bg-black"
                    data-testid="edit-remove-cover">
                    <X size={10} />
                  </button>
                </div>
              ) : (
                <div className="w-28 h-16 rounded-md border border-dashed border-white/15 flex items-center justify-center">
                  <ImageIcon size={18} className="text-white/30" />
                </div>
              )}
              <div className="flex-1">
                <div className="text-xs font-mono uppercase tracking-wider text-white/40 mb-1">Cover image</div>
                <div className="text-[11px] text-white/50 mb-2">Thumbnail shown in the lessons grid.</div>
                <button data-testid="edit-cover-upload" onClick={() => coverRef.current?.click()}
                  disabled={uploading}
                  className="cta-light rounded-md px-3 py-1.5 text-xs font-bold disabled:opacity-50">
                  {form.cover_url ? "Replace cover" : "Upload cover"}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 surface-elev rounded-md p-3">
              <button data-testid="edit-free" onClick={() => setForm({ ...form, is_paid: false, price: 0 })}
                className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider transition ${!form.is_paid ? "bg-white text-black" : "text-white/50"}`}>Free</button>
              <button data-testid="edit-paid" onClick={() => setForm({ ...form, is_paid: true })}
                className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider transition flex items-center gap-1.5 ${form.is_paid ? "cta-green" : "text-white/50"}`}>
                <DollarSign size={12} strokeWidth={2.5} /> Paid
              </button>
              {form.is_paid && (
                <div className="relative ml-auto">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#00E676] font-mono">$</span>
                  <input data-testid="edit-price" type="number" value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="w-32 bg-white/[0.04] border border-white/8 rounded-md pl-7 pr-3 py-1.5 font-mono text-sm text-[#00E676] focus:outline-none focus:border-[#00E676]/40" />
                </div>
              )}
            </div>

            <div className="surface-elev rounded-md p-3 space-y-3">
              <div className="text-[10px] font-mono uppercase tracking-wider text-white/40">Attachments</div>
              <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => uploadEdit(e.target.files?.[0], "video")} />
              <input ref={pdfRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => uploadEdit(e.target.files?.[0], "pdf")} />
              <input ref={slideRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => uploadEdit(e.target.files?.[0], "slide")} />
              <div className="flex flex-wrap items-center gap-1.5">
                <button data-testid="edit-add-video" onClick={() => videoRef.current?.click()} disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold surface hover:bg-white/[0.06] transition disabled:opacity-50">
                  <Plus size={12} /><Video size={13} className="text-[#00E676]" /> Video
                </button>
                <button data-testid="edit-add-pdf" onClick={() => pdfRef.current?.click()} disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold surface hover:bg-white/[0.06] transition disabled:opacity-50">
                  <Plus size={12} /><FileText size={13} className="text-[#00E676]" /> PDF
                </button>
                <button data-testid="edit-add-slide" onClick={() => slideRef.current?.click()} disabled={uploading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold surface hover:bg-white/[0.06] transition disabled:opacity-50">
                  <Plus size={12} /><ImageIcon size={13} className="text-[#00E676]" /> Slide
                </button>
              </div>
              {uploading && (
                <div className="flex items-center gap-3 text-xs text-white/70">
                  <Loader2 size={14} className="animate-spin text-[#00E676]" />
                  <div className="flex-1">
                    <div className="flex justify-between mb-1"><span>Uploading {uploading}…</span><span className="font-mono text-[#00E676]">{uploadPct}%</span></div>
                    <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-[#00E676] transition-all" style={{ width: `${uploadPct}%` }} />
                    </div>
                  </div>
                </div>
              )}
              {(form.videos.length + form.pdfs.length + form.slides.length) > 0 && (
                <div className="space-y-1">
                  {form.videos.map((v, i) => (
                    <div key={`v-${i}`} className="flex items-center gap-2 surface rounded-md px-3 py-2 text-xs">
                      <Video size={13} className="text-[#00E676]" /><span className="flex-1 truncate font-mono">{v.name}</span>
                      <button onClick={() => removeEdit("video", i)} className="text-white/40 hover:text-red-400"><X size={12} /></button>
                    </div>
                  ))}
                  {form.pdfs.map((p, i) => (
                    <div key={`p-${i}`} className="flex items-center gap-2 surface rounded-md px-3 py-2 text-xs">
                      <FileText size={13} className="text-[#00E676]" /><span className="flex-1 truncate font-mono">{p.name}</span>
                      <button onClick={() => removeEdit("pdf", i)} className="text-white/40 hover:text-red-400"><X size={12} /></button>
                    </div>
                  ))}
                  {form.slides.map((s, i) => (
                    <div key={`s-${i}`} className="flex items-center gap-2 surface rounded-md px-3 py-2 text-xs">
                      <ImageIcon size={13} className="text-[#00E676]" /><span className="flex-1 truncate font-mono">{s.name}</span>
                      <button onClick={() => removeEdit("slide", i)} className="text-white/40 hover:text-red-400"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button data-testid="save-lesson-edit" onClick={saveEdit} disabled={saving}
                className="cta-green rounded-md px-5 py-2 text-sm font-bold flex items-center gap-1.5 disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={13} />}
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button onClick={() => setEditing(false)} className="surface rounded-md px-4 py-2 text-sm font-semibold hover:bg-white/[0.06] transition">
                Cancel
              </button>
            </div>
          </div>
        )}

        {lesson.cover_url && !editing && (
          <div className="rounded-xl overflow-hidden mb-5 border border-white/8">
            <img src={lesson.cover_url} alt="" className="w-full max-h-72 object-cover" />
          </div>
        )}

        <div className="flex items-center gap-2 mb-3 text-xs font-mono uppercase tracking-wider flex-wrap">
          <span className="verified-pill px-2 py-0.5 rounded">{lesson.niche}</span>
          <span className="text-white/40 flex items-center gap-1"><Clock size={11} /> {lesson.duration_min} min read</span>
          {lesson.is_paid && (
            <span className="px-2 py-0.5 rounded bg-[#00E676]/10 border border-[#00E676]/25 text-[#00E676] flex items-center gap-1">
              {lesson.has_access ? <><BadgeCheck size={11} /> Unlocked</> : <><Lock size={11} /> ${lesson.price}</>}
            </span>
          )}
          <span className="text-white/30">·</span>
          <span className="text-white/40">{lesson.completed_by?.length || 0} completed</span>
        </div>

        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mb-3">{lesson.title}</h1>
        <p className="text-lg text-white/65 leading-relaxed mb-6">{lesson.description}</p>

        <div className="flex items-center gap-3 mb-8 pb-6 border-b border-white/5">
          <Link to={`/profile/${lesson.author?.user_id}`}>
            <img src={lesson.author?.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${lesson.author?.user_id}`} alt="" className="w-10 h-10 rounded-md object-cover bg-white/5" />
          </Link>
          <div>
            <Link to={`/profile/${lesson.author?.user_id}`} className="text-sm font-semibold hover:text-[#00E676] transition">{lesson.author?.name || "Unknown"}</Link>
              <div className="text-[11px] text-white/40 font-mono">Author{lesson.is_paid ? " &middot; approves each buyer manually" : ""}</div>
          </div>
          {me?.user_id === lesson.author?.user_id && (
            <Link to="/lessons/requests" className="ml-auto text-xs font-semibold cta-green px-3 py-1.5 rounded-md" data-testid="manage-requests-link">
              Manage requests
            </Link>
          )}
        </div>

        {locked ? (
          <div className="surface rounded-xl p-8 text-center" data-testid="lesson-paywall">
            <div className="w-12 h-12 mx-auto rounded-md bg-[#00E676]/10 border border-[#00E676]/25 flex items-center justify-center mb-4">
              <Lock size={20} className="text-[#00E676]" />
            </div>
            <div className="font-heading font-bold text-2xl mb-2">${lesson.price} to unlock</div>
            <p className="text-sm text-white/60 mb-6 max-w-md mx-auto">
              The author manually approves every buyer. Request access — once approved, you&apos;ll see the full playbook, all videos, PDFs, and slides.
            </p>
            {status === "pending" ? (
              <div className="surface-elev rounded-md px-5 py-3 inline-flex items-center gap-2 text-sm">
                <Loader2 size={14} className="animate-spin text-[#00E676]" />
                <span>Request pending mentor approval…</span>
              </div>
            ) : status === "rejected" ? (
              <div className="rounded-md px-5 py-3 inline-flex items-center gap-2 text-sm bg-red-500/10 border border-red-500/25 text-red-300">
                Your previous request was rejected. <button onClick={requestAccess} disabled={requesting} className="underline ml-1">Request again</button>
              </div>
            ) : (
              <button
                data-testid="request-access-btn"
                onClick={requestAccess}
                disabled={requesting}
                className="cta-green rounded-md px-6 py-3 font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              >
                {requesting ? <><Loader2 size={14} className="animate-spin" /> Requesting…</> : <>Request access — ${lesson.price}</>}
              </button>
            )}
          </div>
        ) : (
          <>
            <article className="prose prose-invert max-w-none font-mono text-[15px] text-white/85 leading-relaxed whitespace-pre-wrap mb-8">
              {lesson.content}
            </article>

            {(lesson.videos?.length > 0 || lesson.pdfs?.length > 0 || lesson.slides?.length > 0) && (
              <div className="space-y-4 mb-8" data-testid="lesson-attachments">
                {lesson.videos?.length > 0 && (
                  <section>
                    <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Video size={16} className="text-[#00E676]" /> Videos</h3>
                    <div className="space-y-3">
                      {lesson.videos.map((v, i) => (
                        <div key={i} className="surface rounded-lg overflow-hidden">
                          <video src={v.url} controls playsInline className="w-full max-h-[500px] bg-black" data-testid={`lesson-video-${i}`} />
                          <div className="p-2 text-xs font-mono text-white/55 truncate">{v.name}</div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {lesson.slides?.length > 0 && (
                  <section>
                    <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><ImageIcon size={16} className="text-[#00E676]" /> Slides</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {lesson.slides.map((s, i) => (
                        <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="surface rounded-lg overflow-hidden block hover:opacity-90 transition" data-testid={`lesson-slide-${i}`}>
                          <img src={s.url} alt={s.name} className="w-full h-48 object-cover" />
                        </a>
                      ))}
                    </div>
                  </section>
                )}

                {lesson.pdfs?.length > 0 && (
                  <section>
                    <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><FileText size={16} className="text-[#00E676]" /> PDFs</h3>
                    <div className="space-y-2">
                      {lesson.pdfs.map((p, i) => (
                        <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
                          className="surface rounded-md p-3 flex items-center gap-3 hover:bg-white/[0.04] transition" data-testid={`lesson-pdf-${i}`}>
                          <FileText size={18} className="text-[#00E676] shrink-0" />
                          <span className="flex-1 text-sm truncate">{p.name}</span>
                          <span className="text-[11px] font-mono text-white/40">Open PDF →</span>
                        </a>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}

            <div className="pt-6 border-t border-white/5">
              <button
                data-testid="complete-lesson-btn"
                onClick={complete}
                disabled={lesson.completed_by_me}
                className={`rounded-md px-6 py-3 font-semibold flex items-center gap-2 transition ${
                  lesson.completed_by_me ? "surface text-[#00E676] cursor-default" : "cta-green"
                }`}
              >
                {lesson.completed_by_me ? <><BadgeCheck size={16} strokeWidth={2.5} /> Lesson completed</> : <><Check size={16} strokeWidth={2.5} /> Mark as complete</>}
              </button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
